'use client';
import ReviewerFaceImg from './ReviewerFaceImg';
// ?layout= preview pro cards for keyword-lab topic landings (Founder order 01/10/26; spec m8/leak-landing-2x.html 2.1–2.6).
// Compact head (photo · name ★ rating (n) · area · WhatsApp + call — the SAME WaSmartButton/ContactButton doors as CategoryProCard)
// + the pro's REAL reviews in V5 form (reviewCardName full name, "(n)" + tip, source + dd/MM/yy — lib/reviewsPreview, ReviewSourceLine).
// Sliders = native CSS scroll-snap, no library. Only rendered when a ?layout= preview asks for it (lib/topicLayout.ts).
import { useRef, useState } from 'react';
import Link from 'next/link';
import WaSmartButton from './WaSmartButton';
import ContactButton from './ContactButton';
import ReviewerCountTip from './ReviewerCountTip';
import ReviewSourceLine from './ReviewSourceLine';
import type { ReviewItem } from './ReviewsList';
import { fmtRating } from '@/lib/format';
import { avatarImg } from '@/lib/imgUrl';
import { reviewCardName, reviewerCountLabel, reviewSourceOf } from '@/lib/reviewsPreview';
import type { TopicCardVariant } from '@/lib/topicLayout';

export type TopicProCardProps = {
  variant: Exclude<TopicCardVariant, 'full'>;
  id: number; href: string; firstName: string; fullName: string; phone?: string; whatsappValid?: number | null; gender?: string | null;
  imgSrc: string; rating10: number; reviewCount: number; categorySlug: string; categoryName: string;
  locationStr: string; nationwide?: boolean; reviews: ReviewItem[]; priority?: boolean; dimmed?: boolean; dimNote?: string;
};

export const TOPIC_CARD_CSS = '.tp-sl{display:flex;gap:8px;overflow-x:auto;scroll-snap-type:x mandatory;scrollbar-width:none;overscroll-behavior-x:contain;-webkit-overflow-scrolling:touch}.tp-sl::-webkit-scrollbar{display:none}.tp-sl>*{scroll-snap-align:start;flex:0 0 100%}.tp-sl.peek>*{flex-basis:82%}.tp-clamp2{display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}.tp-car{display:flex;gap:10px;overflow-x:auto;scroll-snap-type:x mandatory;scrollbar-width:none;margin-inline:-16px;padding-inline:16px;scroll-padding-inline:16px;padding-bottom:4px}.tp-car::-webkit-scrollbar{display:none}.tp-car>*{flex:0 0 min(300px,82vw);scroll-snap-align:start}';

function ReviewerFace({ r, name }: { r: ReviewItem; name: string }) {
  return r.reviewer_avatar ? (
    <ReviewerFaceImg src={r.reviewer_avatar} name={name} size={22} fontSize={11} />
  ) : (
    <span className="rounded-full flex items-center justify-center text-white font-bold flex-shrink-0" aria-hidden="true"
      style={{ width: 22, height: 22, fontSize: 11, background: `hsl(${(name.charCodeAt(0) * 37) % 360},60%,42%)` }}>{name[0] || '?'}</span>
  );
}

/** one review, V5: face · full name (n) ……… source · date / text (2 lines) */
function MiniReview({ r, flat = false, lines = 2 }: { r: ReviewItem; flat?: boolean; lines?: 1 | 2 }) {
  const name = reviewCardName(r);
  const src = reviewSourceOf(r.review_source);
  const count = reviewerCountLabel(r.reviewer_google_review_count, src, name, r.reviewer_gender);
  const text = (r.review_text || '').trim();
  return (
    <div className="rounded-xl" style={flat ? {} : { background: '#f1f5fb', padding: '8px 10px' }}>
      <div className="flex items-center gap-1.5 min-w-0">
        <ReviewerFace r={r} name={name} />
        <b className="text-[13.5px] font-extrabold min-w-0 truncate" style={{ color: '#0f172a' }}>{name}</b>
        {count && <span className="flex-shrink-0"><ReviewerCountTip short={count.short} tip={count.tip} /></span>}
        <ReviewSourceLine source={src} date={r.review_date} className="text-xs flex-shrink-0 mr-auto" />
      </div>
      <p className={lines === 2 ? 'tp-clamp2' : 'truncate'} style={{ margin: '3px 0 0', fontSize: 14, lineHeight: 1.45, color: '#1e293b' }}>
        {text || (r.rating != null ? 'דירוג ללא המלצה כתובה' : 'המלצה ללא טקסט')}
      </p>
    </div>
  );
}

function Slider({ reviews, peek = false }: { reviews: ReviewItem[]; peek?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const [idx, setIdx] = useState(0);
  const onScroll = () => {
    const el = ref.current; const c = el?.firstElementChild as HTMLElement | null; if (!el || !c) return;
    setIdx(Math.min(reviews.length - 1, Math.round(Math.abs(el.scrollLeft) / (c.offsetWidth + 8))));
  };
  const go = (i: number) => { const el = ref.current; const c = el?.children[i] as HTMLElement | undefined; if (el && c) c.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' }); };
  return (
    <div className="mt-2">
      <div ref={ref} onScroll={onScroll} className={`tp-sl${peek ? ' peek' : ''}`} aria-label="המלצות — החליקו לעוד" tabIndex={0}>
        {reviews.map((r, i) => <MiniReview key={i} r={r} />)}
      </div>
      {!peek && reviews.length > 1 && (
        <div className="flex gap-1 justify-center mt-1.5" aria-label="מעבר בין המלצות">
          {reviews.map((_, i) => (
            <button key={i} type="button" onClick={() => go(i)} aria-label={`המלצה ${i + 1} מתוך ${reviews.length}`} aria-current={i === idx ? true : undefined} tabIndex={-1}
              style={{ width: (i === idx ? 14 : 6) + 10, height: 24, margin: '-9px -5px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
              {/* 24px-tall hit area, visual dot unchanged; the slider itself is the keyboard stop (tabIndex 0) */}
              <span aria-hidden="true" style={{ width: i === idx ? 14 : 6, height: 6, borderRadius: 3, background: i === idx ? '#2563eb' : '#cbd5e1', transition: 'all .2s' }} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function TopicProCard(p: TopicProCardProps) {
  const small = p.variant === 'tile' || p.variant === 'bubble';
  const av = small ? 48 : 64;
  // the quote card: the strongest written review (highest rating, then longest text)
  const best = [...p.reviews].filter(r => (r.review_text || '').trim()).sort((a, b) => (Number(b.rating ?? 5) - Number(a.rating ?? 5)) || ((b.review_text || '').length - (a.review_text || '').length))[0] || p.reviews[0];
  const area = p.nationwide ? (p.locationStr ? `${p.locationStr} ונותן שירות בכל הארץ` : 'נותן שירות בכל הארץ') : p.locationStr ? `${p.locationStr}${/בלבד$/.test(p.locationStr) ? '' : ' והסביבה'}` : '';
  return (
    <div className="bg-white min-w-0" data-topic-card={p.variant} data-dimmed={p.dimmed ? '1' : undefined}
      style={{ borderRadius: 16, boxShadow: '0 1px 3px rgba(15,23,42,.08)', padding: 12, ...(p.variant === 'tile' ? { borderTop: '4px solid #2563eb' } : { borderInlineStart: '4px solid #2563eb' }) }}>
      {p.dimNote && <span className="inline-flex items-center gap-1 px-2.5 py-1 mb-2 rounded-full text-xs font-bold" style={{ background: '#fff7ed', color: '#9a3412', border: '1px solid #fdba74' }}><span aria-hidden="true">📍</span>{p.dimNote}</span>}
      <div style={{ opacity: p.dimmed ? 0.45 : 1 }}>
        <div className="flex items-center gap-3">
          <Link href={p.href} className="flex-shrink-0">
            <img {...avatarImg(p.imgSrc, av, { kind: 'pro', ctx: { category: p.categoryName, proName: p.firstName } })} width={av} height={av} loading={p.priority ? 'eager' : 'lazy'} decoding="async"
              {...(p.priority ? { fetchPriority: 'high' as const } : {})} className="rounded-full object-cover" style={{ width: av, height: av, border: '2px solid #e0e7ff' }} />
          </Link>
          <div className="flex-1 min-w-0">
            <Link href={p.href} className={p.variant === 'tile' ? 'flex flex-wrap items-baseline gap-x-1 min-w-0' : 'flex items-baseline gap-1 whitespace-nowrap min-w-0'}>
              <h3 className={p.variant === 'tile' ? 'font-black basis-full truncate' : 'font-black truncate'} style={{ fontSize: small ? 16 : 18, color: '#0f172a' }}>{p.firstName}</h3>
              <span className="inline-flex items-baseline gap-0.5 flex-shrink-0"><span style={{ color: '#f59e0b' }}>★</span><span className="font-black" style={{ color: '#0d47a1', fontSize: 15 }}>{fmtRating(p.rating10)}</span></span>
              <span className="flex-shrink-0" style={{ color: '#64748b', fontSize: 13 }}>(<bdi dir="ltr">{p.reviewCount}</bdi>)</span>
            </Link>
            {area && <p className="truncate" style={{ fontSize: 13, color: '#64748b' }}>{area}</p>}
          </div>
          <div className="flex gap-1.5 flex-shrink-0">
            {p.phone && <WaSmartButton proArea={null} proName={p.fullName} proFirstName={p.firstName} proPhone={p.phone} whatsappValid={p.whatsappValid} proId={p.id} proGender={p.gender} categorySlug={p.categorySlug} categoryName={p.categoryName} square />}
            <ContactButton proName={p.fullName} proFirstName={p.firstName} proPhone={p.phone} proId={p.id} proGender={p.gender} categorySlug={p.categorySlug} categoryName={p.categoryName} square />
          </div>
        </div>
        {p.reviews.length > 0 && (
          p.variant === 'slider' || p.variant === 'tile' ? <Slider reviews={p.reviews} />
          : p.variant === 'peek' ? <Slider reviews={p.reviews} peek />
          : p.variant === 'quote' && best ? (() => {
              const name = reviewCardName(best); const src = reviewSourceOf(best.review_source);
              const count = reviewerCountLabel(best.reviewer_google_review_count, src, name, best.reviewer_gender);
              return (
                <div className="mt-2 rounded-xl" style={{ background: '#f1f5fb', padding: '6px 10px' }}>
                  <p className="truncate" style={{ margin: 0, fontSize: 14, color: '#1e293b' }}>“{(best.review_text || '').trim()}”</p>
                  <div className="flex items-center gap-1.5 mt-0.5 min-w-0">
                    <ReviewerFace r={best} name={name} />
                    <span className="text-[12.5px] font-bold truncate min-w-0" style={{ color: '#475569' }}>{name}</span>
                    {count && <span className="flex-shrink-0"><ReviewerCountTip short={count.short} tip={count.tip} /></span>}
                    <ReviewSourceLine source={src} date={best.review_date} className="text-xs flex-shrink-0 mr-auto" />
                  </div>
                </div>
              );
            })()
          : p.variant === 'bubble' && best ? (
            <div className="mt-2 relative rounded-xl" style={{ background: '#f1f5fb', padding: '8px 10px' }}>
              <span aria-hidden="true" style={{ position: 'absolute', top: -7, insetInlineStart: 18, borderInline: '7px solid transparent', borderBottom: '7px solid #f1f5fb' }} />
              <MiniReview r={best} flat />
            </div>
          ) : null
        )}
      </div>
    </div>
  );
}
